import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';

// components
import { Select } from 'components/spaceKit';

// utils
import {
    StatusTagConditionDimension,
    StatusTagConditionMetric,
    getName,
} from '@sprinklr/display-builder/widgets/utils/getStatusTag';
import { getMetricDesignOptionsKey } from '../metricDesignTunables/utils/getMetricDesignOptionsKey';

// types
import { AnalyticsProjection } from '@sprinklr/stories/analytics/AnalyticsRequest';
import { FieldSelectionProps } from './types';
import { DATE_FILTER_DATA_TYPES } from '../../../Panel/Picker/PickerSLADateFilter/constants';

function FieldSelection({ widget, statusTag }: FieldSelectionProps) {
    const handleChange = useCallback(
        selection => {
            if (selection) {
                const isMetric = 'measurementName' in selection;

                runInAction(() => {
                    // switching from metric to dimension and vice versa
                    if (isMetric && statusTag.type === 'DIMENSION') {
                        ((statusTag as unknown) as StatusTagConditionMetric).comparison = '>';
                    } else if ('dimensionName' in selection && statusTag.type === 'METRIC') {
                        ((statusTag as unknown) as StatusTagConditionDimension).comparison = 'IN';
                        ((statusTag as unknown) as StatusTagConditionDimension).value = [];
                    }

                    statusTag.type = isMetric ? 'METRIC' : 'DIMENSION';
                    statusTag.name = isMetric ? selection.id : selection.label;
                });
            }
        },
        [statusTag]
    );

    const options = [
        ...widget.analyticsRequests[0].groupBys.filter(
            // filter out date types for now
            groupBy => !DATE_FILTER_DATA_TYPES.includes(groupBy.groupType as any)
        ),
        ...widget.analyticsRequests[0].projections,
    ].map(groupByOrProjection => {
        return {
            id:
                'measurementName' in groupByOrProjection
                    ? getMetricDesignOptionsKey(
                          (groupByOrProjection as AnalyticsProjection).measurementName,
                          (groupByOrProjection as AnalyticsProjection).aggregateFunction
                      )
                    : groupByOrProjection.resolveName,
            label: getName(groupByOrProjection),
            ...groupByOrProjection,
        };
    });

    const value = options.find(option => {
        const optionId = 'measurementName' in option ? option.id : getName(option);
        return optionId === statusTag.name;
    });

    return <Select value={value} options={options} onChange={handleChange} />;
}

const FieldSelectionHOC = observer(FieldSelection);
export { FieldSelectionHOC as FieldSelection };
